import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams, useSearchParams } from "react-router";
import { useWorkspaceContext } from "@core/context";
import { usePromiseToast, useTranslate } from "@core/hooks";
import { removeAnnotationNote } from "@modules/docs/annotations/services";
import type { ServiceError } from "@core/types/TServiceError";

export function useRemoveAnnotationNote(annotationId: string, noteId: string) {
  const { workspaceId } = useWorkspaceContext();
  const { projectId } = useParams<{ projectId: string }>();
  const [searchParams] = useSearchParams();
  const approvalId = searchParams.get("approval_id") ?? "";
  const queryClient = useQueryClient();
  const { successKey, errorKey } = usePromiseToast();
  const { t } = useTranslate("documents");

  return useMutation({
    mutationFn: () =>
      removeAnnotationNote(workspaceId, projectId!, approvalId, annotationId, noteId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["approval-annotations", workspaceId, projectId, approvalId],
      });
      queryClient.invalidateQueries({
        queryKey: ["annotation-notes", workspaceId, projectId, approvalId],
      });
      successKey(t, "annotations.note-remove-success");
    },
    onError: (err: ServiceError) => {
      errorKey(t, "errors." + err.code, err.fields);
    },
  });
}
